import { Injectable } from '@angular/core';
import { GetTimestampFromTime } from '@dave/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { MaterialGroupEntityFromBackend } from '../../entities/material-group.entity';
import { AppGatewayService } from '../../services/app-gateway.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { MaterialGroupActionTypes } from '../actions/material-group.actions';
import { State } from '../index';
import { getMaterialGroupDictionary } from '../selectors/material-group.selectors';

enum ErrorCodes {
    Add = 'MaterialGroup Hinzufügen fehlgeschlagen',
    Load = 'MaterialGroup Abrufen fehlgeschlagen',
    Modify = 'MaterialGroup Bearbeiten fehlgeschlagen',
    Remove = 'MaterialGroup Löschen fehlgeschlagen',
}

@Injectable()
export class MaterialGroupEffects {
    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayService: AppGatewayService) {}

    AddMaterialGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MaterialGroupActionTypes.CreateRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ ErpMaterialGroupTypeAdd: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return MaterialGroupActionTypes.CreateSuccess({
                                Payload: MaterialGroupEntityFromBackend(res.ErpMaterialGroupTypeAdd.MaterialGroupType),
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        MaterialGroupActionTypes.CreateFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Errors: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    ModifyMaterialGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MaterialGroupActionTypes.Change),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ ErpMaterialGroupTypeEdit: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return MaterialGroupActionTypes.UpdateOne({ Payload: MaterialGroupEntityFromBackend(res.ErpMaterialGroupTypeEdit.MaterialGroupType) });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    LoadMaterialGroups$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(MaterialGroupActionTypes.Load),
                switchMap(({ Payload }) => {
                    const requestPayload = isNotNullOrUndefined(Payload?.updatedSince) ? { UpdatedAt: Payload.updatedSince, WithDeleted: true } : { WithDeleted: true };

                    return this.gatewayService
                        .Request({ ErpMaterialGroupTypeGet: requestPayload })
                        .then((res) => {
                            if (Object.keys(res?.Errors || {}).length === 0) {
                                if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                    this.store$.dispatch(
                                        MaterialGroupActionTypes.UpdateAll({
                                            Payload: res.ErpMaterialGroupTypeGet.MaterialGroupTypes.map((val) => MaterialGroupEntityFromBackend(val)),
                                            updateLatestUpdatedAt: true,
                                        }),
                                    );
                                } else if (res.ErpMaterialGroupTypeGet.MaterialGroupTypes.length) {
                                    firstValueFrom(this.store$.select(getMaterialGroupDictionary)).then((MaterialGroups) => {
                                        const fRes = res.ErpMaterialGroupTypeGet.MaterialGroupTypes.filter((MaterialGroup) => {
                                            const fromState = MaterialGroups[MaterialGroup.Id];
                                            return !fromState || GetTimestampFromTime(fromState.UpdatedAt).toString() !== MaterialGroup.UpdatedAt;
                                        });
                                        if (fRes.length) {
                                            this.store$.dispatch(
                                                MaterialGroupActionTypes.UpdateMany({
                                                    Payload: fRes.map((val) => MaterialGroupEntityFromBackend(val)),
                                                    updateLatestUpdatedAt: true,
                                                }),
                                            );
                                        }
                                    });
                                }
                            } else {
                                throw res.Errors;
                            }
                        })
                        .catch((err) => {
                            this.store$.dispatch(
                                BaseActionTypes.ErrorAction({
                                    Payload: {
                                        ToasterMessage: ErrorCodes.Load,
                                        Err: err,
                                    },
                                }),
                            );
                            // return EMPTY;
                        });
                }),
            );
        },
        { dispatch: false },
    );

    DeleteMaterialGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MaterialGroupActionTypes.Delete),
            switchMap(({ Payload }) =>
                this.gatewayService
                    .Request({ ErpMaterialGroupTypeDelete: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return MaterialGroupActionTypes.RemoveOne({ Payload: +Payload.Id });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Remove,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );
    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MaterialGroupActionTypes.CreateFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
}
